import {useQuery} from '@tanstack/react-query';
import Environment from '../../../environment';

/**
 * Opening explorer. Positions are immutable, so results are cached forever
 * per (fen, db, filters) key; 429s back off for the full minute the API
 * asks for. `keepPreviousData` keeps the previous position's table on
 * screen while the next one loads, matching lichess's own explorer feel.
 *
 * masters db: served by our own self-hosted lila-openingexplorer instance
 * (see opening-explorer/ at the repo root) - no auth needed, no Lichess
 * dependency. Set EXPLORER_MASTERS_BASE_URL in env.local.js/env.production.js.
 *
 * lichess db: still served directly by Lichess (https://lichess.org/api#tag/Opening-Explorer),
 * pending its own self-hosted migration. Requires authentication on
 * explorer.lichess.ovh - set LICHESS_EXPLORER_TOKEN in env.local.js /
 * env.production.js. Generate a token at https://lichess.org/account/oauth/token
 * (no scopes).
 */

export type ExplorerDb = 'masters' | 'lichess';

export type ExplorerSpeed = 'bullet' | 'blitz' | 'rapid' | 'classical';

export const EXPLORER_SPEEDS: ExplorerSpeed[] = [
  'bullet',
  'blitz',
  'rapid',
  'classical',
];
export const EXPLORER_RATINGS = [1400, 1600, 1800, 2000, 2200, 2500];

export type ExplorerConfig = {
  db: ExplorerDb;
  speeds: ExplorerSpeed[];
  ratings: number[];
};

export const DEFAULT_EXPLORER_CONFIG: ExplorerConfig = {
  db: 'masters',
  speeds: ['blitz', 'rapid', 'classical'],
  ratings: [1600, 1800, 2000, 2200, 2500],
};

export type ExplorerMoveRow = {
  uci: string;
  san: string;
  white: number;
  draws: number;
  black: number;
  averageRating?: number;
};

export type ExplorerGame = {
  id: string;
  winner: 'white' | 'black' | null;
  white: {name: string; rating?: number};
  black: {name: string; rating?: number};
  year?: number;
  speed?: string;
};

export type ExplorerData = {
  white: number;
  draws: number;
  black: number;
  moves: ExplorerMoveRow[];
  topGames?: ExplorerGame[];
  recentGames?: ExplorerGame[];
  opening?: {eco: string; name: string} | null;
};

/** Categorised error so the UI can show a precise message. */
export type ExplorerErrorKind = 'auth' | 'rate-limit' | 'server' | 'network';

export class ExplorerError extends Error {
  constructor(
    public readonly kind: ExplorerErrorKind,
    public readonly status?: number,
    message?: string,
  ) {
    super(message ?? `Explorer request failed (${kind})`);
  }
}

function buildUrl(fen: string, config: ExplorerConfig): string {
  const encoded = encodeURIComponent(fen);
  if (config.db === 'masters') {
    const mastersBaseUrl =
      (Environment as any).EXPLORER_MASTERS_BASE_URL ||
      'https://explorer.lichess.ovh/masters';
    return `${mastersBaseUrl}?fen=${encoded}&moves=12&topGames=4`;
  }
  const speeds = config.speeds.join(',');
  const ratings = config.ratings.join(',');
  return `https://explorer.lichess.ovh/lichess?fen=${encoded}&moves=12&recentGames=4&speeds=${speeds}&ratings=${ratings}`;
}

// Exported so other callers hitting explorer.lichess.ovh directly (e.g.
// useExplorerGamePgn's PGN fetch) apply the same bearer token - the lichess
// db requires it on every endpoint, not just the search one below.
export function buildLichessAuthHeaders(db: ExplorerDb): HeadersInit {
  // Our self-hosted masters instance needs no auth - only the lichess db
  // (still served directly by Lichess) requires the bearer token.
  if (db === 'masters') {
    return {};
  }
  const token = (Environment as any).LICHESS_EXPLORER_TOKEN as
    | string
    | undefined;
  if (token) {
    return {Authorization: `Bearer ${token}`};
  }
  return {};
}

export default function useOpeningExplorer(
  fen: string | undefined | null,
  config: ExplorerConfig,
  enabled = true,
) {
  // buildUrl ignores speeds/ratings for the masters db, so they must be
  // left out of the key too - otherwise changing those filters while on
  // masters fragments the cache into entries that all fetch the same data.
  const queryKey =
    config.db === 'masters'
      ? ['lichessExplorer', config.db, fen]
      : ['lichessExplorer', config.db, config.speeds, config.ratings, fen];

  return useQuery<ExplorerData, ExplorerError>(
    queryKey,
    async ({signal}) => {
      let res: Response;
      try {
        res = await fetch(buildUrl(fen as string, config), {
          signal,
          headers: buildLichessAuthHeaders(config.db),
        });
      } catch (networkErr) {
        throw new ExplorerError('network', undefined, String(networkErr));
      }
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new ExplorerError(
            'auth',
            res.status,
            'Lichess explorer requires authentication — set LICHESS_EXPLORER_TOKEN.',
          );
        }
        if (res.status === 429) {
          throw new ExplorerError('rate-limit', 429);
        }
        throw new ExplorerError('server', res.status);
      }
      return res.json();
    },
    {
      enabled: enabled && !!fen,
      staleTime: Infinity,
      keepPreviousData: true,
      retry: (failureCount, error: unknown) => {
        if (error instanceof ExplorerError) {
          // Never retry auth errors — the token is wrong/missing, retrying
          // immediately won't help and wastes requests.
          if (error.kind === 'auth') {
            return false;
          }
          // Rate-limit: retry up to 2× after 61 s (as Lichess asks).
          if (error.kind === 'rate-limit') {
            return failureCount < 2;
          }
          // Transient server/network errors: up to 2 quick retries.
          return failureCount < 2;
        }
        return false;
      },
      retryDelay: (_, error: unknown) => {
        if (error instanceof ExplorerError && error.kind === 'rate-limit') {
          return 61_000;
        }
        // Exponential back-off for other transient errors (1 s → 4 s → …)
        return 1_000;
      },
    },
  );
}
